<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  onActivated,
  onDeactivated,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import conversationWorkflowsAPI from 'dashboard/api/conversationWorkflows';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  buildPinnedBindingsFromTrace,
  writePinnedExecution,
} from './workflowEditorPinnedExecution';
import { worldById } from 'dashboard/routes/dashboard/hub/hubWorldLookup';
import { workflowKindLabel } from './workflowKindLabels';

const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();
const { t } = useI18n();

const workflowId = computed(() => route.params.workflowId);
const executionId = computed(() => route.params.executionId);

const isLoading = ref(true);
const loadError = ref(false);
const payload = ref({});
const graph = ref({ nodes: [], edges: [] });
const selectedIndex = ref(0);

const trace = computed(() =>
  Array.isArray(payload.value.trace) ? payload.value.trace : []
);

const nodesById = computed(() => {
  const nodes = graph.value?.nodes || [];
  const map = {};
  nodes.forEach(n => {
    if (n?.id != null) map[String(n.id)] = n;
  });
  return map;
});

const selectedStep = computed(() => trace.value[selectedIndex.value] || null);

const showTechnical = ref(false);
let requestToken = 0;
const active = ref(true);
const mounted = ref(false);

const humanizeKey = key =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

/** Mesma regra que `ConversationWorkflows::WorkflowVariableInterpolator` no servidor. */
const valueAtBindingPath = (bindings, path) => {
  if (
    !bindings ||
    typeof bindings !== 'object' ||
    path == null ||
    path === ''
  ) {
    return undefined;
  }
  const keys = String(path).split('.');
  let cur = bindings;
  let invalid = false;
  keys.forEach(key => {
    if (cur == null || typeof cur !== 'object') {
      invalid = true;
      return;
    }
    cur = cur[key];
  });
  if (invalid) return undefined;
  return cur;
};

const interpolateWorkflowTemplate = (template, bindings) => {
  if (typeof template !== 'string' || !template) return template;
  if (!bindings || typeof bindings !== 'object') return template;
  const re = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;
  return template.replace(re, (_, path) => {
    const v = valueAtBindingPath(bindings, path);
    if (v === undefined || v === null) return '';
    if (typeof v === 'object') {
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }
    return String(v);
  });
};

const formatScalar = v => {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.length ? `${v.length} itens` : '—';
  if (typeof v === 'object') {
    try {
      const s = JSON.stringify(v);
      return s.length > 280 ? `${s.slice(0, 280)}…` : s;
    } catch {
      return String(v);
    }
  }
  return String(v);
};

const friendlyBindingRows = bindings => {
  const rows = [];
  if (!bindings || typeof bindings !== 'object') return rows;

  const conv = bindings.conversation;
  if (conv && typeof conv === 'object') {
    if (conv.display_id != null) {
      rows.push({
        label: 'Conversa',
        value: `Nº ${conv.display_id}`,
      });
    }
    if (conv.status != null && conv.status !== '') {
      rows.push({ label: 'Estado da conversa', value: String(conv.status) });
    }
    if (conv.priority != null && conv.priority !== '') {
      rows.push({ label: 'Prioridade', value: String(conv.priority) });
    }
  }

  const contact = bindings.contact;
  if (contact && typeof contact === 'object') {
    const name = contact.name || '';
    const phone = contact.phone_number || '';
    const email = contact.email || '';
    const bits = [name, phone || email].filter(Boolean);
    if (bits.length) {
      rows.push({ label: 'Cliente / contato', value: bits.join(' · ') });
    }
  }

  const msg = bindings.message;
  if (msg && typeof msg === 'object' && Object.keys(msg).length) {
    if (msg.content != null && String(msg.content).trim() !== '') {
      const c = String(msg.content);
      rows.push({
        label: 'Mensagem que disparou o passo',
        value: c.length > 220 ? `${c.slice(0, 220)}…` : c,
      });
    }
    if (msg.message_type) {
      rows.push({ label: 'Tipo de mensagem', value: String(msg.message_type) });
    }
  }

  const up = bindings.upstream?.http_response || bindings.upstream?.http;
  if (up && typeof up === 'object') {
    const code = up.status_code ?? up.statusCode;
    if (code != null && code !== '') {
      rows.push({
        label: 'Resposta HTTP anterior (código)',
        value: String(code),
      });
    }
    const body = up.body?.toString?.() || '';
    if (body.length) {
      rows.push({
        label: 'Resposta HTTP anterior (trecho)',
        value: body.length > 180 ? `${body.slice(0, 180)}…` : body,
      });
    }
  }

  const kb = bindings.kanban_item;
  if (kb && typeof kb === 'object' && Object.keys(kb).length) {
    const title = kb.title || kb.item_details?.title || '';
    const stage = kb.funnel_stage || '';
    const parts = [title, stage && `Etapa: ${stage}`].filter(Boolean);
    if (parts.length) {
      rows.push({ label: 'Cartão no Kanban', value: parts.join(' · ') });
    }
  }

  return rows;
};

const friendlyConfigRows = (config, { kind, bindings } = {}) => {
  if (!config || typeof config !== 'object') return [];
  const skip = new Set(['id', 'kind']);
  const interpolateContentKeys = new Set();
  if (kind === 'send_message') interpolateContentKeys.add('message_content');
  if (kind === 'add_private_note')
    interpolateContentKeys.add('private_note_content');

  return Object.entries(config)
    .filter(([k]) => !skip.has(k))
    .map(([k, v]) => {
      if (
        interpolateContentKeys.has(k) &&
        typeof v === 'string' &&
        bindings &&
        typeof bindings === 'object'
      ) {
        return {
          label: humanizeKey(k),
          value: interpolateWorkflowTemplate(v, bindings),
        };
      }
      return {
        label: humanizeKey(k),
        value: formatScalar(v),
      };
    });
};

const friendlyOutputRows = (output, kind) => {
  const rows = [];
  if (!output || typeof output !== 'object') return rows;

  if (kind === 'instagram_public_reply') {
    const delivery = output.delivery_status || 'legacy_unknown';
    rows.push({
      label: t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.DELIVERY.LABEL'),
      value: t(`CONVERSATION_WORKFLOW_EXECUTION_DETAIL.DELIVERY.${delivery}`),
    });
    if (output.reason)
      rows.push({
        label: t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.DELIVERY.REASON'),
        value: t(
          `CONVERSATION_WORKFLOW_EXECUTION_DETAIL.DELIVERY.${output.reason}`
        ),
      });
    if (output.provider_message_id)
      rows.push({
        label: t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.DELIVERY.PROVIDER_ID'),
        value: String(output.provider_message_id),
      });
  }
  if (output.branch_handle != null && output.branch_handle !== '') {
    rows.push({
      label: 'Caminho da condição',
      value: String(output.branch_handle),
    });
  }
  if (output.advanced === true || output.advanced === 'true') {
    rows.push({ label: 'Gatilho', value: 'Acionado; o fluxo seguiu adiante.' });
  }
  if (output.scheduled === true || output.scheduled === 'true') {
    rows.push({
      label: 'Espera agendada',
      value: 'Sim, o fluxo continua após o tempo definido.',
    });
  }
  if (output.wait_seconds != null) {
    rows.push({
      label: 'Tempo de espera',
      value: `${output.wait_seconds} s`,
    });
  } else if (output.wait_minutes != null) {
    rows.push({
      label: 'Tempo de espera (minutos)',
      value: String(output.wait_minutes),
    });
  }
  if (Array.isArray(output.buttons)) {
    rows.push({
      label: 'Aguardando resposta pelos botões',
      value: output.buttons.map(b => b?.title || b?.id).join(' · ') || '—',
    });
  }
  if (output.next_node_id != null && output.next_node_id !== '') {
    rows.push({
      label: 'Próximo passo (referência interna)',
      value: String(output.next_node_id),
    });
  }
  if (
    kind !== 'instagram_public_reply' &&
    (output.ok === true || output.ok === 'true')
  ) {
    rows.push({ label: 'Ação', value: 'Executada sem erro aparente.' });
  }
  if (output.status_code != null && output.status_code !== '') {
    rows.push({
      label: 'Código HTTP da resposta',
      value: String(output.status_code),
    });
  }
  if (output.body != null && String(output.body).trim() !== '') {
    const b = String(output.body);
    rows.push({
      label: 'Corpo da resposta (trecho)',
      value: b.length > 220 ? `${b.slice(0, 220)}…` : b,
    });
  }
  if (output.error != null && output.error !== '') {
    rows.push({ label: 'Erro retornado', value: String(output.error) });
  }
  if (output.message != null && output.message !== '') {
    rows.push({ label: 'Mensagem do sistema', value: String(output.message) });
  }
  if (output.performed === true) {
    rows.push({ label: 'Resultado', value: 'Concluído.' });
  }

  const known = new Set([
    'branch_handle',
    'advanced',
    'scheduled',
    'wait_minutes',
    'wait_seconds',
    'buttons',
    'next_node_id',
    'ok',
    'status_code',
    'body',
    'error',
    'message',
    'performed',
    'headers_json',
  ]);
  Object.entries(output).forEach(([k, v]) => {
    if (known.has(k)) return;
    rows.push({ label: humanizeKey(k), value: formatScalar(v) });
  });

  return rows;
};

const detailBlocks = computed(() => {
  const step = selectedStep.value;
  if (!step) return [];

  const input = step.input && typeof step.input === 'object' ? step.input : {};
  const bindings = input.bindings;
  const config = input.config;
  const stepKind =
    typeof step.kind === 'string' && step.kind !== ''
      ? step.kind
      : config?.kind;

  const blocks = [
    {
      id: 'context',
      title: 'O que o fluxo “enxergava” neste momento',
      hint: 'Resumo da conversa, do cliente e de outros dados usados automaticamente neste passo.',
      rows: friendlyBindingRows(bindings),
    },
    {
      id: 'settings',
      title: 'O que você configurou neste bloco',
      hint: 'Valores definidos no editor do fluxo (textos, opções, URLs, etc.).',
      rows: friendlyConfigRows(config, { kind: stepKind, bindings }),
    },
    {
      id: 'result',
      title: 'O que aconteceu em seguida',
      hint: 'Resultado deste passo: resposta de API, caminho escolhido na condição, confirmação de envio, etc.',
      rows: friendlyOutputRows(
        step.output && typeof step.output === 'object' ? step.output : {},
        stepKind
      ),
    },
  ];

  return blocks;
});

const formatTs = ts => {
  if (ts == null || ts === '') return '—';
  const n = Number(ts);
  if (!Number.isFinite(n)) return '—';
  return new Date(n * 1000).toLocaleString('pt-BR');
};

const stepStatus = step =>
  step.kind === 'instagram_public_reply'
    ? step.output?.delivery_status || 'uncertain'
    : step.status;

const publicReplyLabel = step =>
  t(
    `CONVERSATION_WORKFLOW_EXECUTION_DETAIL.DELIVERY.${step.output?.delivery_status || 'legacy_unknown'}`
  );

const stepStatusClass = status => {
  const map = {
    success: 'border-n-teal-7 bg-n-teal-3/40',
    accepted: 'border-n-teal-7 bg-n-teal-3/40',
    rejected: 'border-n-ruby-7 bg-n-ruby-3/40',
    uncertain: 'border-n-amber-7 bg-n-amber-3/40',
    not_sent: 'border-n-amber-7 bg-n-amber-3/40',
    waiting: 'border-n-amber-7 bg-n-amber-3/40',
    error: 'border-n-ruby-7 bg-n-ruby-3/40',
  };
  return map[status] || 'border-n-slate-6 bg-n-alpha-2';
};

const stepDotClass = status => {
  const map = {
    success: 'bg-n-teal-9',
    accepted: 'bg-n-teal-9',
    rejected: 'bg-n-ruby-9',
    uncertain: 'bg-n-amber-9',
    not_sent: 'bg-n-amber-9',
    waiting: 'bg-n-amber-9',
    error: 'bg-n-ruby-9',
  };
  return map[status] || 'bg-n-slate-9';
};

const nodeTitle = step => {
  if (!step) return '—';
  const id = step.node_id;
  if (id === '__resume__') {
    return step.kind === 'resume_after_reply'
      ? t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.RESUME_AFTER_REPLY')
      : t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.RESUME_AFTER_WAIT');
  }
  // Primeiro o rótulo gravado na execução (o bloco pode ter sido renomeado ou
  // apagado depois), depois o do grafo atual, e só então o nome do tipo.
  if (step.node_label) return String(step.node_label);
  const n = nodesById.value[id];
  if (n?.data) {
    const d = n.data;
    const label = d.label || d.title;
    if (label) return String(label);
  }
  return step.kind ? workflowKindLabel(t, step.kind) : '—';
};

const formatJson = v => {
  if (v == null || v === '') return '';
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const load = async () => {
  requestToken += 1;
  const token = requestToken;
  const context = `${accountId.value}:${workflowId.value || ''}:${executionId.value || ''}`;
  if (!workflowId.value || !executionId.value) {
    payload.value = {};
    graph.value = { nodes: [], edges: [] };
    loadError.value = false;
    isLoading.value = false;
    return;
  }
  isLoading.value = true;
  loadError.value = false;
  try {
    const [exRes, wfRes] = await Promise.all([
      conversationWorkflowsAPI.executionShow(
        workflowId.value,
        executionId.value
      ),
      conversationWorkflowsAPI.show(workflowId.value),
    ]);
    if (
      token !== requestToken ||
      !active.value ||
      context !==
        `${accountId.value}:${workflowId.value || ''}:${executionId.value || ''}`
    )
      return;
    payload.value = exRes.data?.payload ?? {};
    const g = wfRes.data?.payload?.graph;
    graph.value =
      g && typeof g === 'object'
        ? { nodes: g.nodes || [], edges: g.edges || [] }
        : { nodes: [], edges: [] };
    selectedIndex.value = 0;
  } catch (error) {
    if (
      token !== requestToken ||
      !active.value ||
      context !==
        `${accountId.value}:${workflowId.value || ''}:${executionId.value || ''}`
    )
      return;
    loadError.value = true;
    if ([401, 403, 404].includes(error?.response?.status)) {
      payload.value = {};
      graph.value = { nodes: [], edges: [] };
    }
    useAlert(t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.LOAD_ERROR'));
  } finally {
    if (token === requestToken) isLoading.value = false;
  }
};

watch([accountId, workflowId, executionId], () => {
  payload.value = {};
  graph.value = { nodes: [], edges: [] };
  loadError.value = false;
  selectedIndex.value = 0;
  if (active.value) load();
});
onMounted(() => {
  mounted.value = true;
  load();
});
onActivated(() => {
  if (!mounted.value || active.value) return;
  active.value = true;
  load();
});
onDeactivated(() => {
  active.value = false;
  requestToken += 1;
  payload.value = {};
  graph.value = { nodes: [], edges: [] };
  loadError.value = false;
  isLoading.value = false;
  selectedIndex.value = 0;
});
onBeforeUnmount(() => {
  active.value = false;
  requestToken += 1;
});

const navigationTarget = (name, params) => {
  const target = { name, params };
  const fromWorld = route.query?.from_world;
  const fromWorldId =
    typeof fromWorld === 'string' ? worldById(fromWorld)?.id : undefined;
  if (fromWorldId) target.query = { from_world: fromWorldId };
  return target;
};

const goToList = () => {
  const target = navigationTarget('conversation_workflows_executions', {
    accountId: accountId.value,
    workflowId: workflowId.value,
  });
  // Volta para a página da lista de onde a execução foi aberta
  const page = Number(route.query?.page);
  if (Number.isInteger(page) && page > 1)
    target.query = { ...target.query, page: String(page) };
  router.push(target);
};

const selectStep = idx => {
  selectedIndex.value = idx;
  showTechnical.value = false;
};

const canCopyToEditor = computed(() => {
  if (!trace.value.length) return false;
  const hasBindings = trace.value.some(s => {
    const b = s?.input?.bindings;
    return (
      b &&
      typeof b === 'object' &&
      !Array.isArray(b) &&
      Object.keys(b).length > 0
    );
  });
  const hasStructuredOutput = trace.value.some(s => {
    if (
      !s ||
      s.node_type !== 'action' ||
      String(s.status || '') !== 'success'
    ) {
      return false;
    }
    const kind = String(s.kind || '');
    const out = s.output;
    if (!out || typeof out !== 'object') return false;
    if (kind === 'ai_agent') {
      return (
        String(out.message ?? '').trim() !== '' ||
        String(out.error ?? '').trim() !== ''
      );
    }
    if (kind === 'http_request') {
      return (
        (out.body != null && String(out.body).trim() !== '') ||
        (out.status_code != null && String(out.status_code) !== '') ||
        String(out.error ?? '').trim() !== ''
      );
    }
    return false;
  });
  return hasBindings || hasStructuredOutput;
});

const copyToEditor = () => {
  const bindings = buildPinnedBindingsFromTrace(trace.value);
  if (!Object.keys(bindings).length) {
    useAlert(t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.COPY_EMPTY'));
    return;
  }
  writePinnedExecution(accountId.value, workflowId.value, {
    executionId: payload.value.id,
    copiedAt: new Date().toISOString(),
    bindings,
  });
  useAlert(t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.COPY_SUCCESS'));
  router.push(
    navigationTarget('conversation_workflows_edit', {
      accountId: accountId.value,
      workflowId: workflowId.value,
    })
  );
};
</script>

<template>
  <div
    data-testid="page-conversation-workflow-execution-detail"
    class="fixed inset-0 z-[100] flex flex-col bg-n-surface-1 text-n-slate-12 border-t border-n-weak"
  >
    <header
      class="flex flex-shrink-0 items-center gap-2 border-b border-n-weak bg-n-solid-1 px-3 py-2 sm:px-4"
    >
      <button
        type="button"
        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        :title="$t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.BACK')"
        :aria-label="$t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.BACK')"
        @click="goToList"
      >
        <span class="i-ph-arrow-left size-5 shrink-0" aria-hidden="true" />
      </button>
      <div class="min-w-0 flex-1">
        <h1 class="truncate text-base font-medium text-n-slate-12">
          {{
            $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.TITLE', {
              id: payload.id ?? '—',
            })
          }}
        </h1>
        <p class="truncate text-xs text-n-slate-11">
          {{
            $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.META', {
              trigger: payload.trigger_kind
                ? workflowKindLabel(t, payload.trigger_kind)
                : '—',
              conversation: payload.conversation_display_id
                ? `#${payload.conversation_display_id}`
                : payload.conversation_id || '—',
              started: formatTs(payload.started_at),
            })
          }}
        </p>
      </div>
      <Button
        v-if="trace.length > 0"
        slate
        outline
        size="sm"
        :label="$t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.COPY')"
        :disabled="!canCopyToEditor || isLoading"
        @click="copyToEditor"
      />
      <Button
        slate
        outline
        size="sm"
        :label="$t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.REFRESH')"
        :is-loading="isLoading"
        @click="load"
      />
    </header>

    <div
      v-if="
        ['unconfirmed', 'delivery_attention'].includes(payload.display_status)
      "
      role="status"
      class="border-b border-n-amber-6 bg-n-amber-2 px-4 py-3 text-sm text-n-amber-11"
    >
      {{
        $t(
          `CONVERSATION_WORKFLOW_EXECUTION_DETAIL.NOTICE.${payload.display_status}`
        )
      }}
    </div>

    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center text-n-slate-11"
    >
      {{ $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.LOADING') }}
    </div>

    <div
      v-else-if="loadError && trace.length === 0"
      class="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center text-n-slate-11"
      role="alert"
    >
      <p>{{ $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.LOAD_ERROR') }}</p>
      <Button
        :label="$t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.RETRY')"
        slate
        outline
        size="sm"
        @click="load"
      />
    </div>

    <div
      v-else-if="trace.length === 0"
      class="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center text-n-slate-11"
    >
      <p>{{ $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.EMPTY_TITLE') }}</p>
      <p class="max-w-md text-xs">
        {{ $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.EMPTY_DESCRIPTION') }}
      </p>
    </div>

    <div v-else class="flex min-h-0 flex-1 flex-col lg:flex-row">
      <div
        v-if="loadError"
        class="flex shrink-0 items-center justify-between gap-3 rounded-xl border border-n-ruby-6 bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
        role="alert"
      >
        <span>{{
          $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.LOAD_ERROR')
        }}</span>
        <Button
          :label="$t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.RETRY')"
          slate
          outline
          size="sm"
          @click="load"
        />
      </div>
      <aside
        class="flex max-h-[40vh] w-full shrink-0 flex-col overflow-hidden border-b border-n-weak bg-n-solid-1 lg:max-h-none lg:w-[min(22rem,100%)] lg:border-b-0 lg:border-r"
      >
        <div class="shrink-0 border-b border-n-weak px-3 py-2">
          <div class="text-xs font-medium text-n-slate-12">
            {{ $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.STEPS') }} ({{
              trace.length
            }})
          </div>
          <div class="mt-0.5 text-[11px] leading-snug text-n-slate-10">
            {{ $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.STEPS_HINT') }}
          </div>
        </div>
        <ul class="min-h-0 flex-1 overflow-y-auto p-2">
          <li v-for="(step, idx) in trace" :key="idx">
            <button
              type="button"
              class="mb-1 flex w-full rounded-xl border-l-4 px-2 py-2 text-left transition-colors hover:bg-n-alpha-2"
              :class="[
                stepStatusClass(stepStatus(step)),
                selectedIndex === idx ? 'ring-2 ring-n-teal-8' : '',
              ]"
              :aria-label="nodeTitle(step)"
              :aria-pressed="selectedIndex === idx"
              @click="selectStep(idx)"
            >
              <span
                class="mt-1 mr-2 size-2 shrink-0 rounded-full"
                :class="stepDotClass(stepStatus(step))"
                aria-hidden="true"
              />
              <span class="min-w-0 flex-1">
                <span
                  class="block truncate text-sm font-medium text-n-slate-12"
                  :title="nodeTitle(step)"
                >
                  {{ nodeTitle(step) }}
                </span>
                <span class="block truncate font-mono text-xs text-n-slate-11">
                  {{ step.node_type }} · {{ step.kind }}
                </span>
                <span class="block text-xs text-n-slate-10">
                  <span
                    v-if="step.kind === 'instagram_public_reply'"
                    class="block"
                  >
                    {{ publicReplyLabel(step) }}
                  </span>
                  {{ formatTs(step.at) }}
                </span>
              </span>
            </button>
          </li>
        </ul>
      </aside>

      <section
        class="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-3 sm:p-4"
      >
        <div
          v-if="selectedStep?.error_message"
          class="rounded-xl border border-n-ruby-7 bg-n-ruby-3/30 px-3 py-2 text-sm text-n-ruby-12"
        >
          <span class="font-medium">{{
            $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.STEP_ERROR')
          }}</span>
          {{ selectedStep.error_message }}
        </div>

        <div class="flex flex-col gap-4">
          <article
            v-for="block in detailBlocks"
            :key="block.id"
            class="rounded-2xl border border-n-weak bg-n-solid-1"
          >
            <header class="border-b border-n-weak px-4 py-3">
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ block.title }}
              </h2>
              <p class="mt-1 text-xs leading-relaxed text-n-slate-11">
                {{ block.hint }}
              </p>
            </header>
            <div v-if="block.rows.length" class="divide-y divide-n-weak px-4">
              <div
                v-for="(row, ri) in block.rows"
                :key="`${block.id}-${ri}`"
                class="grid grid-cols-1 gap-1 py-3 sm:grid-cols-12 sm:gap-3"
              >
                <div class="text-sm font-medium text-n-slate-11 sm:col-span-4">
                  {{ row.label }}
                </div>
                <div
                  class="whitespace-pre-wrap break-words text-sm text-n-slate-12 sm:col-span-8"
                >
                  {{ row.value }}
                </div>
              </div>
            </div>
            <p v-else class="px-4 py-6 text-center text-sm text-n-slate-10">
              {{ $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.NO_DATA') }}
            </p>
          </article>
        </div>

        <div class="rounded-2xl border border-n-weak/80 bg-n-alpha-2">
          <button
            type="button"
            class="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2"
            @click="showTechnical = !showTechnical"
          >
            <span>{{
              $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.TECHNICAL')
            }}</span>
            <span class="text-n-slate-11">{{ showTechnical ? '▲' : '▼' }}</span>
          </button>
          <div
            v-if="showTechnical"
            class="space-y-3 border-t border-n-weak px-4 pb-4 pt-3"
          >
            <div>
              <div
                class="mb-1 text-[11px] font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.RAW_INPUT') }}
              </div>
              <pre
                class="max-h-48 overflow-auto rounded-xl bg-n-slate-2 p-3 font-mono text-[11px] leading-relaxed text-n-slate-12"
                >{{ formatJson(selectedStep?.input) }}</pre
              >
            </div>
            <div>
              <div
                class="mb-1 text-[11px] font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ $t('CONVERSATION_WORKFLOW_EXECUTION_DETAIL.RAW_OUTPUT') }}
              </div>
              <pre
                class="max-h-48 overflow-auto rounded-xl bg-n-slate-2 p-3 font-mono text-[11px] leading-relaxed text-n-slate-12"
                >{{ formatJson(selectedStep?.output) }}</pre
              >
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
